/**
 * LPR · ¿la placa tipeada sirve para que la camara de salida la empareje? Helpers PUROS
 * (sin Angular), con su spec al lado (plate-quality.spec.ts).
 *
 * 🔴 Es la MISMA regla del API (api/helpers/lpr-similar.js, isInvalidPlate) y del reporte
 * LPR (estado `invalid_plate` en get-report.sql): si cambia alla, cambia aca. Una placa
 * NO sirve si, ya normalizada (solo A-Z y 0-9, en mayusculas):
 *   - tiene menos de 5 caracteres;
 *   - es una palabra que el guardia escribe cuando no hay placa real (NA, UBER, TEMP...);
 *   - es un solo caracter repetido (00000, XXXXXX);
 *   - contiene BIKE, NOPLATE, NOTAG, UNKNOWN o TEST.
 *
 * Con una placa asi la visita nunca va a tener salida por camara, y en el reporte cae en
 * "Placa invalida", fuera del KPI. En la garita (scan-dialog) solo se AVISA: el guardia
 * puede admitir igual, y solo donde sirve: una ENTRADA en vehiculo por una puerta de un
 * sitio que marca salidas con camara LPR.
 *
 * Este archivo no importa nada: lo usa SecurityService, que va en el bundle inicial.
 */

/** Largo minimo (en caracteres A-Z/0-9) de una placa con la que la camara pueda emparejar. */
export const PLATE_MIN_LENGTH = 5;

/** Palabras que el guardia escribe cuando no hay placa real (comparadas enteras). */
export const PLATE_JUNK_WORDS = ['BIKE', 'UBER', 'LYFT', 'NA', 'NONE', 'NOPLATE', 'PAPER', 'TEMP', 'TAG',
    'DEALER', 'NOTAG', 'TAXI', 'RES', 'UNKNOWN', 'TEST'];

/** Pedazos que delatan una placa de relleno aunque vengan pegados a otra cosa ("NOTAG1"). */
export const PLATE_JUNK_PARTS = ['BIKE', 'NOPLATE', 'NOTAG', 'UNKNOWN', 'TEST'];

/**
 * Por que una placa no sirve:
 *   empty     no hay placa
 *   short     menos de PLATE_MIN_LENGTH caracteres
 *   word      es entera una palabra de relleno (PAPER, NOTAG...)
 *   contains  contiene un pedazo de relleno (BIKE, NOPLATE, NOTAG, UNKNOWN, TEST)
 *   repeated  un solo caracter repetido
 */
export type PlateInvalidReason = 'empty' | 'short' | 'word' | 'contains' | 'repeated';

/** Placa normalizada como la compara el API: mayusculas, solo A-Z y 0-9 ('zzt-12 3' -> 'ZZT123'). */
export function plateNormalize(value: any): string {
    return typeof value === 'string' ? value.toUpperCase().replace(/[^A-Z0-9]/g, '') : '';
}

/** Motivo por el que la placa no sirve para la camara, o null si sirve. */
export function plateInvalidReason(value: any): PlateInvalidReason | null {
    const plate = plateNormalize(value);
    if (plate === '') {
        return 'empty';
    }
    if (plate.length < PLATE_MIN_LENGTH) {
        return 'short';
    }
    if (PLATE_JUNK_WORDS.indexOf(plate) >= 0) {
        return 'word';
    }
    if (PLATE_JUNK_PARTS.some(part => plate.indexOf(part) >= 0)) {
        return 'contains';
    }
    if (/^(.)\1*$/.test(plate)) {
        return 'repeated';
    }
    return null;
}

/** ¿La placa NO sirve para la camara? (vacia incluida). */
export function plateIsInvalid(value: any): boolean {
    return plateInvalidReason(value) !== null;
}

/** ¿Ese `pass` del scan-dialog es una admision? (true o 'printPass'; false es Turn Away). */
export function plateAdmits(pass: any): boolean {
    return pass === true || pass === 'printPass';
}

/**
 * ¿La puerta marca salidas de visitantes con camara LPR? Lee la respuesta de GET
 * /shared/reports/access/lpr (la misma consulta del filtro "Salida control vehicular" del
 * Visitor Log): `{lpr: {checkout: true}}`. Cualquier otra cosa (sin LPR, error) = false.
 */
export function plateLprCheckout(res: any): boolean {
    return !!res && !!res.lpr && res.lpr.checkout === true;
}

/**
 * Cuanto vale un "no marca salidas" (o un error) antes de volver a preguntar. El API
 * responde `checkout: false` tambien cuando falla de su lado (y durante su pausa de 10 min
 * si falta un patch): un false no puede quedar para toda la sesion.
 */
export const PLATE_LPR_CHECKOUT_FALSE_TTL_MS = 10 * 60 * 1000;

/**
 * Cache por puerta (uuid) de plateLprCheckout, para la garita (la usa SecurityService):
 *   - `load` (al elegir la puerta y al abrir el dialogo) pregunta si no se sabe nada, o si
 *     lo que se sabe es un false (o un error) de hace PLATE_LPR_CHECKOUT_FALSE_TTL_MS o mas.
 *     Un true queda toda la sesion. Con la consulta en vuelo no pregunta otra vez.
 *   - `get` responde sin esperar: lo que se sabe, o false si todavia no respondio.
 * La puerta llega como el objeto de SecurityService.gate (su uuid en `value`) o como uuid.
 * `now` es el reloj (el spec pasa uno de mentira).
 */
export class PlateLprCheckoutCache {
    private known: { [gate: string]: { value: boolean, at: number } } = {};
    private pending: { [gate: string]: boolean } = {};

    constructor(private now: () => number = () => Date.now()) {
    }

    load(gate: any, fetch: (id: string, done: (res: any) => void, fail: () => void) => void): void {
        const id = gateId(gate);
        if (id === null || this.pending[id]) {
            return;
        }
        const entry = this.known[id];
        if (entry && (entry.value || this.now() - entry.at < PLATE_LPR_CHECKOUT_FALSE_TTL_MS)) {
            return;
        }
        this.pending[id] = true;
        fetch(id, res => {
            this.known[id] = { value: plateLprCheckout(res), at: this.now() };
            delete this.pending[id];
        }, () => {
            this.known[id] = { value: false, at: this.now() };
            delete this.pending[id];
        });
    }

    get(gate: any): boolean {
        const id = gateId(gate);
        return id !== null && !!this.known[id] && this.known[id].value === true;
    }
}

function gateId(gate: any): string | null {
    const id = gate && typeof gate === 'object' ? gate.value : gate;
    return typeof id === 'string' && id !== '' ? id : null;
}

/**
 * ¿Hay que preguntarle al guardia si la placa esta bien antes de admitir? Devuelve el
 * motivo, o null si no hay nada que preguntar.
 *
 *   - Solo una entrada que LPR puede seguir (`tracked` true): la admision de un grupo o
 *     evento escribe group_invitation_scan, no scanqr, y la camara solo empareja scanqr.
 *   - Solo donde la camara puede marcar la salida: `lprCheckout` true (plateLprCheckout de
 *     la puerta). En una comunidad sin LPR la placa no le importa a nadie mas.
 *   - Solo una ENTRADA (`entranceType` '1'): LPR sigue entradas, no salidas.
 *   - Solo EN VEHICULO (`walking` en false, la misma condicion con la que el dialogo
 *     muestra el campo de placa). A pie no hay placa que revisar.
 *   - Placa vacia: solo si el sitio exige placa (`mandatoryPlate`, con la misma lectura que
 *     la regla de la plantilla). Si no la exige, una visita sin placa es valida.
 *   - Cualquier otra placa que no sirva (plateInvalidReason), venga tipeada o precargada
 *     del registro del invitado (ahi suele quedar el "NA" de una visita anterior).
 */
export function plateSuspect(entry: {
    tracked: any,
    lprCheckout: any,
    entranceType: any,
    walking: any,
    plate: any,
    mandatoryPlate: any,
}): PlateInvalidReason | null {
    const e: any = entry || {};
    if (e.tracked !== true || e.lprCheckout !== true || String(e.entranceType) !== '1' || e.walking !== false) {
        return null;
    }
    const reason = plateInvalidReason(e.plate);
    if (reason === 'empty' && !e.mandatoryPlate) {
        return null;
    }
    return reason;
}

/** Clave i18n (SEC_CONTROL, sin namespace) del texto de la confirmacion para cada motivo. */
export function plateSuspectMessageKey(reason: PlateInvalidReason | null): string {
    switch (reason) {
        case 'empty': return 'PLATE_SUSPECT_EMPTY';
        case 'word': return 'PLATE_SUSPECT_WORD';
        case 'contains': return 'PLATE_SUSPECT_CONTAINS';
        case 'repeated': return 'PLATE_SUSPECT_REPEATED';
        default: return 'PLATE_SUSPECT_SHORT';
    }
}
